import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from './Layout';

const ProtectedRoute = ({
  children, adminOnly = false, adminOrEmployeeOnly = false, internalStaffOnly = false, mobileAdminOnly = false,
  financeOnly = false, salesOnly = false, marketingOnly = false, ceoStrategicOnly = false,
  chairmanOnly = false, growthStackOnly = false, paymentsOnly = false,
  coreTeamOnly = false, marketingViewOnly = false, dutiesOnly = false, myAnalyticsOnly = false, homeOnly = false,
}) => {
  const {
    isAuthenticated, loading, isAdmin, isEmployee, isAnyStaff, isInternalStaff, hasMobileAdminAccess,
    hasFinanceAccess, isSalesLeadership, isMarketingLeadership, showCeoStrategicMetrics,
    isChairman, hasGrowthStackAccess, canRecordPayments, user, isCoreTeam, canViewMarketing, hasDuties,
    isCoach, canOpenMyAnalytics, canOpenHome, coversLoaded,
  } = useAuth();
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check localStorage as fallback if state hasn't updated yet
  useEffect(() => {
    // Give React a moment to update state after login
    const timer = setTimeout(() => {
      setCheckingAuth(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Check both state and localStorage for authentication
  const token = localStorage.getItem('token');
  const savedUser = localStorage.getItem('user');
  const actuallyAuthenticated = isAuthenticated || (token && savedUser);

  if (loading || checkingAuth || (isAuthenticated && !coversLoaded)) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!actuallyAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Get user role from state or localStorage
  let userRole = user?.role?.toUpperCase();
  if (!userRole && savedUser) {
    try {
      const parsedUser = JSON.parse(savedUser);
      userRole = parsedUser?.role?.toUpperCase();
    } catch (e) {
      console.error('Error parsing user from localStorage:', e);
    }
  }

  // Each active flag contributes one OR'd condition — a route guarded by
  // both adminOnly and salesOnly (e.g. /agents) is reachable by EITHER,
  // not only by a user who satisfies both. Mirrors Layout.js's nav filter.
  const anyFlagSet = (
    adminOnly || adminOrEmployeeOnly || internalStaffOnly || mobileAdminOnly || financeOnly || salesOnly ||
    marketingOnly || ceoStrategicOnly || chairmanOnly || growthStackOnly || paymentsOnly ||
    coreTeamOnly || marketingViewOnly || dutiesOnly || myAnalyticsOnly || homeOnly
  );
  if (anyFlagSet) {
    const checks = [];
    if (adminOnly) checks.push(isAdmin());
    if (adminOrEmployeeOnly) checks.push(isAnyStaff());
    if (internalStaffOnly) checks.push(isInternalStaff());
    if (mobileAdminOnly) checks.push(hasMobileAdminAccess());
    if (financeOnly) checks.push(hasFinanceAccess());
    if (salesOnly) checks.push(isSalesLeadership());
    if (marketingOnly) checks.push(isMarketingLeadership());
    if (ceoStrategicOnly) checks.push(showCeoStrategicMetrics());
    if (chairmanOnly) checks.push(isChairman());
    if (growthStackOnly) checks.push(hasGrowthStackAccess());
    if (paymentsOnly) checks.push(canRecordPayments());
    if (coreTeamOnly) checks.push(isCoreTeam());
    if (marketingViewOnly) checks.push(canViewMarketing());
    if (dutiesOnly) checks.push(hasDuties());
    if (myAnalyticsOnly) checks.push(canOpenMyAnalytics());
    if (homeOnly) checks.push(canOpenHome());
    if (!checks.some(Boolean)) {
      // The CMO has no client lists; his home is the marketing plan. The Coach's is My Analytics.
      const role = user?.role?.toUpperCase();
      return <Navigate to={role === 'CMO' ? '/marketing' : role === 'COACH' ? '/my-analytics' : '/students'} replace />;
    }
  }
  // The Coach sees no client records (Q7a): any route without a flag that lets them in goes home.
  if (isCoach() && !anyFlagSet && !['/settings'].includes(window.location.pathname)) {
    return <Navigate to="/my-analytics" replace />;
  }

  return <Layout>{children}</Layout>;
};

export default ProtectedRoute;

